/**
 * How this console writes money, in one place, because it writes almost nothing else.
 *
 * CLAUDE.md section 2: **"Money is always shown with the currency code and full minor units (for
 * example, KES 1,184,000.00)."** That sentence is this module. Every amount on all three boards
 * goes through `money()`, and the design's own worked example is the first case `tests/money.test.ts`
 * pins.
 *
 * Four decisions underneath it, and each is a way the obvious implementation gets it wrong.
 *
 * **The minor unit is read from the currency, not assumed to be two.** `Intl.NumberFormat` knows
 * that KES, USD and EUR have two and that JPY has none, and this platform is multi-tenant across
 * East Africa — UGX and RWF also have none. Dividing by 100 and formatting to two places would
 * write `UGX 41,200.00` for an integer number of shillings, which is a hundredfold error that reads
 * as precision.
 *
 * **Nothing is divided by 100 in floating point.** AGENTS.md: never use floating-point values for
 * money. `amountMinor` arrives as a JSON number — the contract's own type, so it is what the
 * console is handed — and the decimal point is placed by splitting the integer's own decimal
 * spelling, never by `value / 100`. `0.07 * 100` is `7.000000000000001`; the same class of error at
 * the other end of the scale is the cent a reconciliation comes out short by.
 *
 * **The currency code is written out, in front, and is not the symbol.** `Intl`'s own
 * `currencyDisplay: "code"` puts the ISO code where the symbol would go and inserts a non-breaking
 * space, and in some locales puts the code after the number. The design writes `KES 980.00` with
 * the code first and an ordinary space, on every page of this console, so the code is placed here
 * and `Intl` is asked only to group the digits of the major half.
 *
 * **The minus sign belongs to the row's kind, not to a line's side.** The ledger's own grammar is an
 * unsigned amount with `Dr` or `Cr` beside it, which is what the design writes on three of its four
 * ledger rows — `KES 980.00 Cr`, `KES 980.00 Dr`, `KES 30.00 Dr`. The fourth, `KES −540.00` with no
 * side at all, is a *reversal*, where the design is writing the correction's net effect rather than
 * one line of a double entry. A screen that negated every debit would turn a balanced entry into a
 * column of mixed signs that does not sum to zero. So `money()` takes the sign as an option, the
 * adjustments board passes it and the ledger board does not.
 *
 * The character is U+2212 MINUS SIGN, which is what the design writes. A hyphen is not a minus and
 * at 13.5px they are different glyphs.
 */

/** U+2212. The design's own character on `KES −540.00`, and not a hyphen. */
export const MINUS = "−";

/**
 * One `Intl.NumberFormat` per currency, cached, and consulted for one thing only.
 *
 * Constructing one is the expensive part, and a board of 200 journal transactions with two lines
 * each would construct 400. What it is asked is `minimumFractionDigits`, which for a
 * currency-styled formatter is that currency's own minor-unit exponent from the CLDR data — the
 * authority on ISO 4217 that a table written here would be a second, stale opinion about.
 *
 * **What it cannot tell us, measured rather than assumed.** `Intl` throws `RangeError` only on a
 * code that is not three ASCII letters: `ZZ`, `zzzz`, `K1S` and `""` all throw, and `ZZZ` — a
 * well-formed code ISO 4217 does not assign — comes back with CLDR's default of two decimal places,
 * exactly as `KES` does. So there is no way through this API to tell "two decimals because ISO says
 * so" from "two decimals because nothing said".
 *
 * That is accepted rather than worked around, and the reason is where the real guard is: all three
 * contracts validate `currency` as `/^[A-Z]{3}$/` and every amount on these boards carries the
 * currency the service stored beside it. A code that is well-formed and unassigned is a row the
 * platform wrote wrong, not a formatting decision this module can rescue — and the alternative, a
 * hand-written table of assigned codes in a browser bundle, would be a second opinion about ISO
 * 4217 that goes stale silently. Recorded in R-35 rather than left as an implied claim.
 */
const formatters = new Map<string, Intl.NumberFormat | undefined>();

function formatterFor(currency: string): Intl.NumberFormat | undefined {
  if (formatters.has(currency)) return formatters.get(currency);
  let made: Intl.NumberFormat | undefined;
  try {
    made = new Intl.NumberFormat("en-GB", { style: "currency", currency });
  } catch {
    // A malformed code throws `RangeError`. Cached as absent so the throw happens once per code
    // rather than once per row, and handled in `money()` rather than papered over.
    made = undefined;
  }
  formatters.set(currency, made);
  return made;
}

/**
 * How many minor units make one major unit of this currency, as a count of decimal places.
 *
 * `undefined` only when the code is not three ASCII letters — see `formatterFor` for why that is
 * the whole of what can be detected here.
 */
export function minorUnitDigits(currency: string): number | undefined {
  return formatterFor(currency)?.resolvedOptions().minimumFractionDigits;
}

/** Digit grouping for the major half, with no currency and no fraction of its own. */
const grouping = new Intl.NumberFormat("en-GB", { useGrouping: true, maximumFractionDigits: 0 });

/**
 * One amount, written the way every rule on this platform requires: the code, then the full minor
 * units.
 *
 * @param amountMinor the integer the contract publishes. `lines[].amountMinor` on FIN-02 and
 *   FIN-05, `amountMinor` on FIN-04 — all three are JSON integers in the currency's minor unit.
 * @param currency the ISO 4217 code the same response publishes beside it. Never defaulted: a
 *   console that assumed KES would write the wrong code on a Ugandan tenant's board and the digits
 *   beside it would be right, which is the worst shape a money bug can have.
 * @param options.negative write it as a reduction — `KES −540.00`.
 *
 * Returns the raw integer with its code and the words `minor units` in the two cases where placing
 * a decimal point would be a guess: a currency code that is not three ASCII letters, and an
 * `amountMinor` that is not an integer. Both are contract violations — all three listings validate
 * the code and publish the amount as an integer — and a board that quietly rounded either would be
 * hiding the thing this module exists to get right. A well-formed but unassigned code is **not** in
 * that set and cannot be: see `formatterFor`.
 */
export function money(
  amountMinor: number,
  currency: string,
  options: { readonly negative?: boolean } = {},
): string {
  const sign = options.negative === true ? MINUS : "";
  const digits = minorUnitDigits(currency);
  if (digits === undefined || !Number.isInteger(amountMinor)) {
    return `${currency} ${sign}${String(amountMinor)} minor units`;
  }

  /*
   * The decimal point, placed by splitting a string rather than by dividing a number.
   *
   * `amountMinor` is an integer, so its decimal spelling is exact. Splitting that spelling at the
   * currency's own minor-unit boundary gives the two halves with no arithmetic on the amount at all,
   * where `amountMinor / 100` is a binary float that is only *nearly* the right decimal.
   *
   * `padStart(digits + 1, "0")` is what makes 7 minor units read `0.07` rather than `.7`: the pad
   * guarantees at least one digit before the split.
   */
  const unsigned = String(Math.abs(amountMinor)).padStart(digits + 1, "0");
  const major = unsigned.slice(0, unsigned.length - digits);
  const fraction = digits === 0 ? "" : `.${unsigned.slice(unsigned.length - digits)}`;

  return `${currency} ${sign}${grouping.format(Number(major))}${fraction}`;
}

/**
 * A posting line's side, as the design writes it: `Cr` and `Dr`.
 *
 * FIN-02 and FIN-05 both publish `lines[].side` as `"debit" | "credit"`. The design's ledger rows
 * read `KES 980.00 Cr` and `KES 980.00 Dr`, which is the accountant's abbreviation and is what a
 * agent reads; the published word goes on the row's title for anybody who does not.
 *
 * Typed over the published union rather than over `string`, so a third side added to the contract is
 * a build failure here instead of a blank cell on a ledger.
 */
export function sideAbbreviation(side: "debit" | "credit"): "Dr" | "Cr" {
  return side === "debit" ? "Dr" : "Cr";
}
